import {
  BadGatewayError,
  BadRequestError,
  ForbiddenError,
  GatewayTimeoutError,
  InternalServerError,
  MethodNotAllowedError,
  NotFoundError,
  NotImplementedError,
  PayloadTooLargeError,
  ServiceUnavailableError,
  TooManyRequestsError,
  UnauthorizedError,
  UnprocessableEntityError,
  UnsupportedMediaTypeError,
  UntiledError,
} from './http-client.errors';
import type {
  Response,
  Result,
} from './http-client.types';

/**
 * Проверить, выполнился ли запрос успешно.
 *
 * @param response - ответ от сервера.
 */
export function checkIsResponseSuccess(response: Response): boolean {
  return response.status >= 200 && response.status < 300;
}

/**
 * Обработчик ответа от сервера по умолчанию.
 *
 * @param response - ответ от сервера.
 */
export function defaultResponseMiddleware<R extends Result>(response: Response): R {
  if (checkIsResponseSuccess(response)) {
    return response.result;
  }

  // В респонсе может приходить пустая строка как message, например от nginx.
  // Далее в ошибках мы ожидаем undefined как пустое значение, чтобы переписать его статусом.
  // Поэтому последним значением в логическом операторе оставляем undefined, при пустом response.message.
  const message: string | undefined = response.result?.message || response.message || undefined;
  switch (response.status) {
    case 400:
      throw new BadRequestError(message, response.details);
    case 401:
      throw new UnauthorizedError(message, response.details);
    case 403:
      throw new ForbiddenError(message, response.details);
    case 404:
      throw new NotFoundError(message, response.details);
    case 405:
      throw new MethodNotAllowedError(message, response.details);
    case 413:
      throw new PayloadTooLargeError(message, response.details);
    case 415:
      throw new UnsupportedMediaTypeError(message, response.details);
    case 422:
      throw new UnprocessableEntityError(message, response.details);
    case 429:
      throw new TooManyRequestsError(message, response.details);
    case 500:
      throw new InternalServerError(message, response.details);
    case 501:
      throw new NotImplementedError(message, response.details);
    case 502:
      throw new BadGatewayError(message, response.details);
    case 503:
      throw new ServiceUnavailableError(message, response.details);
    case 504:
      throw new GatewayTimeoutError(message, response.details);
    default:
      throw new UntiledError(message || 'Untiled Error!', response.status, response.details);
  }
}

/**
 * Обработчик ошибки по умолчанию.
 *
 * @param error - ошибка.
 */
export function defaultErrorMiddleware(error: unknown): never {
  throw error;
}
